/**
 * Runtime content-version sync.
 *
 * On app boot (and periodically thereafter) we ask `/api/health/cms` for
 * the LIVE `contentVersion` reported by the CMS. If it differs from the
 * value we last saw, every cached CMS response in our namespace is dropped
 * and a `cms:version-mismatch` event is emitted. The next call to
 * `loadCms` will therefore fetch fresh data.
 *
 * This makes cache invalidation event-driven end-to-end:
 *   editor saves → Payload hook bumps site-settings.contentVersion →
 *   /api/health/cms reflects it → this module sees the mismatch →
 *   frontend cache is wiped → loadCms refetches.
 *
 * Configuration:
 *   REACT_APP_CMS_HEALTH_PATH        — endpoint (default /api/health/cms)
 *   REACT_APP_CMS_VERSION_POLL_MS    — interval between polls (0 = boot-only)
 *
 * Polling is intentionally lightweight (a single JSON GET) and only fires
 * when the tab is visible.
 */
import RESILIENCE_CONFIG from "@/config/resilience";
import { emit, CMS_EVENTS } from "./cmsTelemetry";

const STORE_KEY = () => `${RESILIENCE_CONFIG.cacheNamespace}:live-content-version`;
const CACHE_PREFIX = () => `${RESILIENCE_CONFIG.cacheNamespace}:cache:`;

const parseIntOr = (raw, fallback) => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const POLL_MS = parseIntOr(
  typeof process !== "undefined" && process.env && process.env.REACT_APP_CMS_VERSION_POLL_MS,
  60_000
);

const readStore = () => {
  try { return window.localStorage.getItem(STORE_KEY()); } catch { return null; }
};
const writeStore = (v) => {
  try { window.localStorage.setItem(STORE_KEY(), v); } catch { /* ignore */ }
};

/** Wipe every entry in the cache namespace. */
function purgeCacheNamespace(reason) {
  if (typeof window === "undefined") return 0;
  let purged = 0;
  try {
    const prefix = CACHE_PREFIX();
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    for (const k of keys) {
      window.localStorage.removeItem(k);
      purged++;
      emit(CMS_EVENTS.CACHE_INVALIDATE, { key: k, reason });
    }
  } catch { /* ignore */ }
  return purged;
}

/**
 * One-shot version check. Returns the live version (or null on failure).
 */
export async function syncContentVersion() {
  if (typeof window === "undefined") return null;
  try {
    const r = await fetch(RESILIENCE_CONFIG.healthPath, { credentials: "omit" });
    if (!r.ok) return null;
    const body = await r.json();
    const live = body && body.contentVersion;
    if (!live || typeof live !== "string") return null;

    const known = readStore();
    if (known && known !== live) {
      emit(CMS_EVENTS.VERSION_MISMATCH, { cached: known, expected: live });
      const purged = purgeCacheNamespace("live-version-mismatch");
      emit(CMS_EVENTS.CACHE_INVALIDATE, { key: "*", reason: "live-version-mismatch", purged });
    }
    writeStore(live);
    return live;
  } catch {
    return null;
  }
}

/**
 * Wire the sync loop: fire once at boot, then poll on an interval and on
 * `visibilitychange` (so a tab that woke up catches up immediately).
 * Returns a stop function.
 */
export function startVersionSync() {
  if (typeof window === "undefined") return () => {};
  let timer = null;
  let stopped = false;

  const run = () => { if (!stopped) syncContentVersion(); };

  run();
  if (POLL_MS > 0) {
    timer = setInterval(() => {
      if (document.visibilityState === "visible") run();
    }, POLL_MS);
  }
  const onVisible = () => { if (document.visibilityState === "visible") run(); };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    stopped = true;
    if (timer) clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
